// src/components/roads/RoadWorksReportPanel.js
// --- UPDATE (FlowSense road AI/ML pass) ---------------------------------------
//   Fixes
//   • Work orders were requested from `/work-orders?road_id=` — the API is mounted at
//     /workorders, so the list was always empty. Now GET /roads/:id/work-orders
//     (typed road_id + legacy asset_id jobs, with QA audit counts).
//   • `wo.id.substring` crashed on numeric ids; logistics fields could be undefined.
//   • Switching roads quickly showed the previous road's data (stale responses) —
//     every load is tagged and late responses are dropped.
//   • Error state with retry (it failed silently before).
//   New
//   • FlowSense AI tab (score, ranked findings with actions, ML segment evaluation,
//     Ask FlowSense AI / Ollama with rules fallback, guide).
//   • Inspections tab + tick-box inspection checklist (notes, measurements, feedback)
//     for the road and for every road work order; QA audit per work order.
//   • Live: road_update / work_order_events / asphalt_telemetry refresh the report
//     quietly (debounced, filtered to this road).
// --- END UPDATE -----------------------------------------------------------------
// --- UPDATE (RoadWorksPage tab integration)
//   • `embedded` — rendered inside the page's "Road Works Report" tab (no outer margin).
//   • `onDataChanged` — called after an inspection is submitted, a QA audit completes or the
//     ML segment evaluation runs, so the page's KPIs / inventory refresh too.
//   • Road switch resets the inner tab to FlowSense AI; ML evaluation errors no longer replace
//     the whole report with an error banner (shown inline, report stays usable).
// --- END UPDATE
// --- UPDATE (Drive Mode AI review)
//   • New last tab "Drive AI detections": photos from the tech app's Drive Mode with the AI boxes,
//     damage per segment (count + type), click a photo to enlarge and confirm / correct / reject —
//     answers train the detection model and recalculate segment health.
//   • Sliced Segments table: new "DRIVE AI" column (detections per segment by type).
//   • FlowSense AI tab: the road-damage detection model (status, accuracy, training data, Train).
// --- END UPDATE
// --- UPDATE (Completed work)
//   • New last tab "Completed work": every job the crews finished and every submitted inspection on
//     this road, with the Tech-app photos (before / during / after / inspection), resolution notes,
//     technician, dates and time taken, checklist findings, QA, asphalt and parts — plus the FlowSense
//     AI evidence score and sign-off check (RoadCompletedWorkPanel, GET /roads/:id/completed-work).
//   • Linked Work Orders: completed jobs get a "Completed work & photos" button that opens that job.
// --- END UPDATE
// --- UPDATE (roads v1.3 — terrain & ponding)
//   • New tab "Terrain & ponding" (3rd): TessaDEM ground profile of the road with the forecast water level,
//     per-segment ground (start · mid · end), grade, standing water, when it starts / how long it stays, linked drain,
//     "Refresh elevation" and "Recompute forecast" (RoadTerrainPondingTab).
//   • Sliced Segments table: GROUND (TessaDEM) and WATER (forecast) columns.
//   • FlowSense AI findings for ponding / missing terrain run their action (open the tab, raise a drainage job).
// --- END UPDATE
import React, { useState, useEffect, useCallback, useMemo, useRef, memo } from 'react';
import PropTypes from 'prop-types';
import {
    Box, Flex, HStack, VStack, Text, Badge, SimpleGrid, Stat, StatLabel, StatNumber,
    Table, Thead, Tbody, Tr, Th, Td, Progress, Icon, Tabs, TabList, TabPanels, Tab, TabPanel,
    useColorModeValue, Button, Tooltip, Select,
    Alert, AlertIcon, AlertDescription, Skeleton, Stack, IconButton, useDisclosure, Tag,
} from '@chakra-ui/react';
import {
    MdLocalShipping, MdTimeline, MdAssignment,
    MdDirectionsWalk, MdRefresh,
    MdAutoAwesome, MdChecklist, MdVerified, MdOpenInNew, MdAdd,
} from 'react-icons/md';
import { FaRoad } from 'react-icons/fa';
import { apiFetch } from './page/shared';
import { useNavigate } from 'react-router-dom';
import useFlowSenseLive from '../../hooks/useFlowSenseLive';
import { openCreateWorkOrder } from '../work_orders/workOrderCatalog';
import { analyzeRoadReport, roadFollowUpDraft, isOpenWo } from './roadAiIntel';
import RoadWorksAiHelper from './RoadWorksAiHelper';
import RoadInspectionChecklist from './RoadInspectionChecklist';
import RoadWorkOrderQaModal from './RoadWorkOrderQaModal';
// --- UPDATE (Drive Mode AI review)
import RoadDriveDetectionsPanel from './drive/RoadDriveDetectionsPanel';
import RoadVisionModelPanel from './drive/RoadVisionModelPanel';
import { getDriveOverview, damageInfo } from './drive/driveAiApi';
// --- END UPDATE
// --- UPDATE (Completed work)
import RoadCompletedWorkPanel from './RoadCompletedWorkPanel';
import { MdTaskAlt, MdPhotoLibrary } from 'react-icons/md';
// --- END UPDATE
// --- UPDATE (roads v1.3)
import { MdTerrain, MdWaterDrop } from 'react-icons/md';
import RoadTerrainPondingTab from './ponding/RoadTerrainPondingTab';
import { bandMeta as pondBand, fmtDepth } from './ponding/pondingUi';
// --- END UPDATE

const EMPTY_LOGISTICS = { asphaltLogs: [], equipment: [], totalTonsDelivered: 0, summary: null };
const unwrap = (res) => (Array.isArray(res) ? res : (res?.data ?? []));
const shortId = (id) => String(id ?? '').substring(0, 8);
const pciColor = (v) => (v < 50 ? 'red' : v < 75 ? 'orange' : 'green');
const RATING_COLOR = { excellent: 'green', good: 'teal', fair: 'yellow', poor: 'orange', critical: 'red' };
const TEMPLATE_LABEL = { routine: 'Routine survey', pothole_repair: 'Pothole QA', paving_qa: 'Paving QA', line_marking: 'Marking QA', drainage: 'Drainage', sidewalk_curb: 'Sidewalk & curb', signage: 'Signage' };
// --- UPDATE (Completed work): index of the last tab (keep in step with the <Tab> order)
// --- UPDATE (roads v1.3): "Terrain & ponding" inserted as the 3rd tab — indexes after it moved by one
const TAB_TERRAIN = 2;
const TAB_DRIVE = 7;
const TAB_COMPLETED = 8;
// --- END UPDATE
const isDoneWo = (wo) => ['completed', 'closed'].includes(String(wo?.status || '').toLowerCase());
// --- END UPDATE
// --- UPDATE (Phone AI Helpdesk round 3): "Open in Work Orders" pointed at /admin/work-orders, but the
// route in src/routes.js is /admin/workorders — both links landed on a blank page.
// --- END UPDATE
const isPavingJob = (wo) => /pav|pothole|reinstat|resurf|overlay/i.test(`${wo?.category || ''} ${wo?.title || ''}`);

const RoadWorksReportPanel = ({ activeRoadId, roadName, embedded = false, onDataChanged }) => {
    const [segments, setSegments] = useState([]);
    const [subAssets, setSubAssets] = useState([]);
    const [workOrders, setWorkOrders] = useState([]);
    const [selectedWoId, setSelectedWoId] = useState('');
    const [logistics, setLogistics] = useState(EMPTY_LOGISTICS);
    const [isLoading, setIsLoading] = useState(true);
    const [road, setRoad] = useState(null);
    const [checklists, setChecklists] = useState([]);
    const [loadError, setLoadError] = useState(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isLogisticsLoading, setIsLogisticsLoading] = useState(false);
    const [mlEvaluation, setMlEvaluation] = useState(null);
    const [isEvaluating, setIsEvaluating] = useState(false);
    const [checklistTarget, setChecklistTarget] = useState(null);
    const [qaWorkOrder, setQaWorkOrder] = useState(null);
    const [tabIndex, setTabIndex] = useState(0);
    const { isOpen: isChecklistOpen, onOpen: onChecklistOpen, onClose: onChecklistClose } = useDisclosure();
    const { isOpen: isQaOpen, onOpen: onQaOpen, onClose: onQaClose } = useDisclosure();
    const navigate = useNavigate();
    const loadSeqRef = useRef(0);
    const logisticsSeqRef = useRef(0);
    const selectedWoRef = useRef('');
    selectedWoRef.current = selectedWoId;
    const mountedRef = useRef(true);
    useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);
    
    const [evalError, setEvalError] = useState(null);
    const onDataChangedRef = useRef(onDataChanged);
    onDataChangedRef.current = onDataChanged;
    const notifyChanged = useCallback(() => { try { onDataChangedRef.current?.(); } catch { /* page refresh is best-effort */ } }, []);
    const loadRoadDataRef = useRef(null);
    const handleReportChanged = useCallback(() => { loadRoadDataRef.current?.(true); notifyChanged(); }, [notifyChanged]);
    const outerMt = embedded ? 0 : 6;
    // --- UPDATE (Drive Mode AI review): detections per segment for the segments table + tab count
    const [driveBySegment, setDriveBySegment] = useState({});
    const [driveTotals, setDriveTotals] = useState(null);
    const [driveKey, setDriveKey] = useState(0);
    useEffect(() => {
        let alive = true;
        if (!activeRoadId) { setDriveBySegment({}); setDriveTotals(null); return undefined; }
        getDriveOverview({ roadId: activeRoadId, limit: 1 })
            .then((d) => {
                if (!alive) return;
                const map = {};
                (d?.segments || []).forEach((s) => { if (s.segment_id) map[String(s.segment_id)] = s; });
                setDriveBySegment(map);
                setDriveTotals(d?.totals || null);
            })
            .catch(() => { if (alive) { setDriveBySegment({}); setDriveTotals(null); } });
        return () => { alive = false; };
    }, [activeRoadId, driveKey, segments]);
    const handleDriveChanged = useCallback(() => { setDriveKey((k) => k + 1); handleReportChanged(); }, [handleReportChanged]);
    // --- END UPDATE
    // --- UPDATE (Completed work)
    const [completedFocus, setCompletedFocus] = useState(null);
    const openCompletedJob = useCallback((woId) => { setCompletedFocus(woId); setTabIndex(TAB_COMPLETED); }, []);
    const clearCompletedFocus = useCallback(() => setCompletedFocus(null), []);
    // --- END UPDATE

    const cardBg = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const headerBg = useColorModeValue('gray.50', 'gray.900');
    const mutedColor = useColorModeValue('gray.500', 'gray.400');

    // Logistics for single work order
    const loadLogistics = useCallback(async (woId) => {
        if (!woId) { setLogistics(EMPTY_LOGISTICS); return; }
        const seq = ++logisticsSeqRef.current;
        setIsLogisticsLoading(true);
        try {
            const logRes = await apiFetch(`/roads/work-orders/${encodeURIComponent(woId)}/logistics`).catch(() => ({ data: null }));
            if (seq !== logisticsSeqRef.current || !mountedRef.current) return;
            const d = logRes?.data || {};
            setLogistics({
                asphaltLogs: Array.isArray(d.asphaltLogs) ? d.asphaltLogs : [],
                equipment: Array.isArray(d.equipment) ? d.equipment : [],
                totalTonsDelivered: Number(d.totalTonsDelivered || 0),
                summary: d.summary || null,
            });
        } finally {
            if (seq === logisticsSeqRef.current && mountedRef.current) setIsLogisticsLoading(false);
        }
    }, []);

    const loadRoadData = useCallback(async (silent = false) => {
        if (!activeRoadId) return;
        const seq = ++loadSeqRef.current;
        if (silent) setIsRefreshing(true); else { setIsLoading(true); setLoadError(null); }
        const id = encodeURIComponent(activeRoadId);
        try {
            const [roadRes, segRes, subRes, woRes, ckRes] = await Promise.allSettled([
                apiFetch(`/roads/${id}`),
                apiFetch(`/roads/${id}/segments`),
                apiFetch(`/roads/${id}/sub-assets`),
                apiFetch(`/roads/${id}/work-orders?limit=50`),
                apiFetch(`/roads/${id}/inspection-checklists?limit=20`),
            ]);
            if (seq !== loadSeqRef.current || !mountedRef.current) return;
            const val = (r) => (r.status === 'fulfilled' ? r.value : null);
            if ([segRes, woRes].every((r) => r.status === 'rejected')) {
                throw new Error(segRes.reason?.message || 'Road Works report could not be loaded.');
            }
            setRoad(val(roadRes)?.data || null);
            setSegments(unwrap(val(segRes)));
            setSubAssets(unwrap(val(subRes)));
            setChecklists(unwrap(val(ckRes)));
            const wos = unwrap(val(woRes));
            setWorkOrders(wos);
            setLoadError(null);

            const keep = wos.find((w) => String(w.id) === String(selectedWoRef.current));
            const nextWo = keep ? keep.id : (wos[0]?.id ?? '');
            setSelectedWoId(nextWo);
            loadLogistics(nextWo);
        } catch (err) {
            if (seq !== loadSeqRef.current || !mountedRef.current) return;
            if (!silent) setLoadError(err?.message || 'Road Works report could not be loaded.');
        } finally {
            if (seq === loadSeqRef.current && mountedRef.current) { setIsLoading(false); setIsRefreshing(false); }
        }
    }, [activeRoadId, loadLogistics]);
    loadRoadDataRef.current = loadRoadData;

    useEffect(() => {
        setRoad(null); setSegments([]); setSubAssets([]); setWorkOrders([]); setChecklists([]);
        setSelectedWoId(''); setLogistics(EMPTY_LOGISTICS); setMlEvaluation(null);
        setTabIndex(0); setEvalError(null);
        loadRoadData(false);
    }, [loadRoadData]);

    const liveFilter = useCallback((name, data) => {
        const p = data?.payload || data || {};
        const rid = p.road_id ?? p.roadId ?? p.id ?? null;
        if (name === 'asphalt_telemetry') return !p.work_order_id || workOrders.some((w) => String(w.id) === String(p.work_order_id));
        if (name.startsWith('bus:')) return !rid || String(rid) === String(activeRoadId);
        if (rid && String(rid) === String(activeRoadId)) return true;
        const woId = p.workOrderId ?? p.work_order_id;
        return !!woId && workOrders.some((w) => String(w.id) === String(woId));
    }, [activeRoadId, workOrders]);
    useFlowSenseLive(['roads', 'workOrders', 'asphalt_telemetry'], () => loadRoadData(true), { debounceMs: 1500, filter: liveFilter, enabled: !!activeRoadId });

    const handleSelectWo = (woId) => {
        setSelectedWoId(woId);
        loadLogistics(woId);
    };

    const selectedWo = useMemo(() => workOrders.find((w) => String(w.id) === String(selectedWoId)) || null, [workOrders, selectedWoId]);
    const roadInfo = useMemo(() => road || { id: activeRoadId, name: roadName }, [road, activeRoadId, roadName]);
    const analysis = useMemo(() => (isLoading ? null : analyzeRoadReport({ road: roadInfo, segments, subAssets, workOrders, logistics, checklists, mlEvaluation })),
        [isLoading, roadInfo, segments, subAssets, workOrders, logistics, checklists, mlEvaluation]);
    const mlBySegment = useMemo(() => Object.fromEntries((mlEvaluation?.segments || []).map((r) => [r.segment_id, r])), [mlEvaluation]);
    const openCount = useMemo(() => workOrders.filter(isOpenWo).length, [workOrders]);
    // --- UPDATE (Completed work)
    const completedCount = useMemo(() => workOrders.filter(isDoneWo).length, [workOrders]);
    const submittedCount = useMemo(() => checklists.filter((c) => c.status === 'submitted').length, [checklists]);
    const workOrderIds = useMemo(() => workOrders.map((w) => String(w.id)), [workOrders]);
    // --- END UPDATE

    const openChecklist = useCallback((target = {}) => {
        setChecklistTarget(target);
        onQaClose();
        onChecklistOpen();
    }, [onChecklistOpen, onQaClose]);
    
    const openQa = useCallback((wo) => { setQaWorkOrder(wo); onQaOpen(); }, [onQaOpen]);

    const evaluateSegments = useCallback(async () => {
        if (!activeRoadId) return;
        setIsEvaluating(true);
        setEvalError(null);
        const roadAtStart = activeRoadId;
        try {
            const res = await apiFetch(`/roads/${encodeURIComponent(activeRoadId)}/segments/evaluate`, { method: 'POST', body: JSON.stringify({ use_ml: true }) });
            if (mountedRef.current && roadAtStart === activeRoadId) setMlEvaluation(res?.data || null);
            loadRoadData(true);
            notifyChanged();
        } catch (err) {
            if (mountedRef.current) setEvalError(err?.message || 'Segment evaluation failed.');
        } finally {
            if (mountedRef.current) setIsEvaluating(false);
        }
    }, [activeRoadId, loadRoadData, notifyChanged]);

    const handleAction = useCallback((f) => {
        const wo = f.workOrderId ? workOrders.find((w) => String(w.id) === String(f.workOrderId)) : null;
        switch (f.action) {
            case 'paving': case 'pothole': case 'sidewalk': {
                const worst = analysis?.worstSegments || [];
                openCreateWorkOrder(navigate, roadFollowUpDraft({
                    road: roadInfo, action: f.action,
                    description: [`Raised from Road Works AI helper: ${f.title}.`, f.detail, ...worst.slice(0, 5).map((w) => `- ${w.id} (${w.station}): PCI ${w.pci}, ${w.potholes} pothole(s)`)].filter(Boolean).join('\n'),
                }));
                break;
            }
            case 'open_wo': navigate(`/admin/workorders?search=${encodeURIComponent(f.workOrderId)}`); break;
            case 'qa': if (wo || selectedWo) openQa(wo || selectedWo); break;
            case 'checklist': openChecklist({ checklistId: f.checklistId }); break;
            case 'evaluate': evaluateSegments(); break;
            // --- UPDATE (roads v1.3)
            case 'ponding': case 'elevation': setTabIndex(TAB_TERRAIN); break;
            case 'drainage':
                openCreateWorkOrder(navigate, roadFollowUpDraft({
                    road: roadInfo, action: 'drainage',
                    description: [`Raised from Road Works AI helper: ${f.title}.`, f.detail].filter(Boolean).join('\n'),
                }));
                break;
            // --- END UPDATE
            default: break;
        }
    }, [workOrders, analysis, roadInfo, navigate, selectedWo, openQa, openChecklist, evaluateSegments]);

    if (!activeRoadId) {
        return (
            <Box bg={cardBg} borderRadius="2xl" borderWidth="1px" borderColor={borderColor} p={5} mt={outerMt}>
                <Text fontSize="sm" color={mutedColor}>Select a road to see its works report.</Text>
            </Box>
        );
    }

    return (
        <Box bg={cardBg} borderRadius="2xl" borderWidth="1px" borderColor={borderColor} p={5} boxShadow="sm" mt={outerMt} aria-label={`Road works report for ${roadName || road?.name || activeRoadId}`}>
            <Flex justify="space-between" align="center" mb={4} wrap="wrap" gap={3}>
                <HStack spacing={3}>
                    <Box bg="blue.500" p={2.5} borderRadius="xl" color="white">
                        <Icon as={FaRoad} boxSize={5} />
                    </Box>
                    <Box>
                        <HStack spacing={2}>
                            <Text fontSize="md" fontWeight="800">Road Infrastructure & Operations Report</Text>
                            {analysis && analysis.score !== null && (
                                <Tooltip hasArrow label={analysis.headline}>
                                    <Badge colorScheme={analysis.grade.color} cursor="help" aria-label={`Road score ${analysis.score}`}>
                                        {analysis.score} · {analysis.grade.label}
                                    </Badge>
                                </Tooltip>
                            )}
                        </HStack>
                        <Text fontSize="xs" color={mutedColor}>
                            Asset: <b>{roadName || road?.name || activeRoadId}</b> • Segments, Logistics, Inspections & Work Orders
                        </Text>
                    </Box>
                </HStack>
                <HStack spacing={2}>
                    <Button size="xs" colorScheme="blue" variant="outline" leftIcon={<Icon as={MdChecklist} />} onClick={() => openChecklist({ category: 'Inspection' })}>
                        New inspection
                    </Button>
                    <Button size="xs" leftIcon={<Icon as={MdRefresh} />} onClick={() => loadRoadData(false)} isLoading={isLoading || isRefreshing}>
                        Refresh Telemetry
                    </Button>
                </HStack>
            </Flex>

            {loadError && (
                <Alert status="error" borderRadius="lg" mb={3}>
                    <AlertIcon />
                    <AlertDescription flex={1} fontSize="sm">{loadError}</AlertDescription>
                    <Button size="xs" onClick={() => loadRoadData(false)}>Retry</Button>
                </Alert>
            )}

            {evalError && (
                <Alert status="warning" borderRadius="lg" mb={3}>
                    <AlertIcon />
                    <AlertDescription flex={1} fontSize="sm">AI segment evaluation failed: {evalError}</AlertDescription>
                    <Button size="xs" onClick={evaluateSegments} isLoading={isEvaluating}>Retry</Button>
                </Alert>
            )}

            {isLoading ? (
                <Stack spacing={3} aria-label="Loading road report">
                    <Skeleton h="32px" borderRadius="lg" />
                    <Skeleton h="180px" borderRadius="lg" />
                </Stack>
            ) : (
                <Tabs colorScheme="blue" variant="soft-rounded" size="sm" index={tabIndex} onChange={setTabIndex} isLazy>
                    <TabList bg={headerBg} p={1} borderRadius="xl" overflowX="auto" flexWrap={{ base: 'nowrap', md: 'wrap' }}>
                        <Tab><Icon as={MdAutoAwesome} mr={1.5} /> FlowSense AI</Tab>
                        <Tab><Icon as={MdTimeline} mr={1.5} /> Sliced Segments ({segments.length})</Tab>
                        {/* --- UPDATE (roads v1.3) — TAB_TERRAIN */}
                        <Tab><Icon as={MdTerrain} mr={1.5} /> Terrain &amp; ponding{analysis?.stats?.pondingSegments ? ` (${analysis.stats.pondingSegments} wet)` : ''}</Tab>
                        {/* --- END UPDATE */}
                        <Tab><Icon as={MdDirectionsWalk} mr={1.5} /> Sub-Assets & Sidewalks ({subAssets.length})</Tab>
                        <Tab><Icon as={MdLocalShipping} mr={1.5} /> Asphalt Logistics & Fleet</Tab>
                        <Tab><Icon as={MdAssignment} mr={1.5} /> Linked Work Orders ({workOrders.length}{openCount ? ` · ${openCount} open` : ''})</Tab>
                        <Tab><Icon as={MdChecklist} mr={1.5} /> Inspections ({checklists.length})</Tab>
                        {/* --- UPDATE (Drive Mode AI review) */}
                        <Tab><Icon as={MdAutoAwesome} mr={1.5} /> Drive AI detections ({driveTotals?.total ?? 0}{driveTotals?.pending ? ` · ${driveTotals.pending} to review` : ''})</Tab>
                        {/* --- END UPDATE */}
                        {/* --- UPDATE (Completed work) — keep last (TAB_COMPLETED) */}
                        <Tab><Icon as={MdTaskAlt} mr={1.5} /> Completed work ({completedCount} job{completedCount === 1 ? '' : 's'} · {submittedCount} inspection{submittedCount === 1 ? '' : 's'})</Tab>
                        {/* --- END UPDATE */}
                    </TabList>

                    <TabPanels mt={3}>
                        {/* TAB 0: FLOWSENSE AI */}
                        <TabPanel p={0}>
                            <RoadWorksAiHelper 
                                analysis={analysis} 
                                road={roadInfo} 
                                workOrder={selectedWo} 
                                onAction={handleAction}
                                onEvaluate={evaluateSegments} 
                                isEvaluating={isEvaluating} 
                                mlEvaluation={mlEvaluation} 
                            />
                            {/* --- UPDATE (Drive Mode AI review): detection model + training */}
                            <Box mt={4}><RoadVisionModelPanel /></Box>
                            {/* --- END UPDATE */}
                        </TabPanel>

                        {/* TAB 1: ROAD SEGMENT BREAKUP */}
                        <TabPanel p={0}>
                            {segments.length === 0 ? (
                                <Text p={4} fontSize="xs" color={mutedColor}>No segments yet — map the road polyline to slice it into stations.</Text>
                            ) : (
                                <Box overflowX="auto" maxH="380px">
                                    <Table size="sm" variant="simple">
                                        <Thead bg={headerBg} position="sticky" top={0} zIndex={1}>
                                            <Tr>
                                                <Th>SEGMENT</Th>
                                                <Th>STATION RANGE</Th>
                                                <Th>CROWN ELEVATION</Th>
                                                <Th>GROUND (TESSADEM)</Th>{/* --- UPDATE (roads v1.3) */}
                                                <Th>WATER</Th>{/* --- UPDATE (roads v1.3) */}
                                                <Th>POTHOLES</Th>
                                                <Th>CRACKING</Th>
                                                <Th>RUTTING</Th>
                                                <Th>PCI SCORE</Th>
                                                {mlEvaluation && <Th>AI TREATMENT</Th>}
                                                <Th>DRIVE AI</Th>{/* --- UPDATE (Drive Mode AI review) */}
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {segments.map((s) => {
                                                const pci = Number(s.segment_pci ?? 0);
                                                const ml = mlBySegment[s.id];
                                                return (
                                                    <Tr key={s.id}>
                                                        <Td fontWeight="bold" fontFamily="mono" fontSize="xs">{s.id}</Td>
                                                        <Td fontSize="xs">
                                                            {Number(s.station_start_m || 0).toFixed(0)}m – {Number(s.station_end_m || 0).toFixed(0)}m
                                                        </Td>
                                                        {/* --- UPDATE (roads v1.6): crown from TessaDEM (DEM) or a survey (SURVEY); the old 3.50 m default is not shown */}
                                                        <Td fontFamily="mono" fontSize="xs" whiteSpace="nowrap">
                                                            {s.elevation_crown_m != null && s.elevation_crown_source !== 'legacy_default' ? (
                                                                <Tooltip hasArrow isDisabled={!s.elevation_crown_source}
                                                                    label={s.elevation_crown_source === 'tessadem'
                                                                        ? 'Road surface at the segment midpoint from TessaDEM (≈30 m terrain model, ±1–2 m). A surveyed crown replaces it.'
                                                                        : s.elevation_crown_source === 'survey' ? 'Surveyed crown — never overwritten by the terrain model.'
                                                                            : 'The road\'s single elevation — replaced by TessaDEM on the next elevation backfill.'}>
                                                                    <span>
                                                                        {`${Number(s.elevation_crown_m).toFixed(2)}m`}
                                                                        {s.elevation_crown_source === 'tessadem' && <Badge ml={1} fontSize="2xs" colorScheme="teal" variant="subtle">DEM</Badge>}
                                                                        {s.elevation_crown_source === 'survey' && <Badge ml={1} fontSize="2xs" colorScheme="green" variant="subtle">SURVEY</Badge>}
                                                                    </span>
                                                                </Tooltip>
                                                            ) : (
                                                                <Tooltip hasArrow label="Not measured yet — run Admin → Elevation → Road segments (TessaDEM).">
                                                                    <span>—</span>
                                                                </Tooltip>
                                                            )}
                                                        </Td>
                                                        {/* --- END UPDATE */}
                                                        {/* --- UPDATE (roads v1.3): TessaDEM ground + forecast water */}
                                                        <Td fontFamily="mono" fontSize="xs" whiteSpace="nowrap">
                                                            {s.ground_start_elev_m != null || s.ground_end_elev_m != null
                                                                ? <Tooltip hasArrow label={`Start ${s.ground_start_elev_m ?? '—'} m · mid ${s.ground_mid_elev_m ?? '—'} m · end ${s.ground_end_elev_m ?? '—'} m · grade ${s.ground_slope_pct ?? '—'} %`}>
                                                                    <Text as="span">{s.ground_start_elev_m ?? '—'} → {s.ground_end_elev_m ?? '—'} m{s.ground_slope_pct != null ? ` (${Number(s.ground_slope_pct) > 0 ? '+' : ''}${s.ground_slope_pct}%)` : ''}</Text>
                                                                </Tooltip>
                                                                : <Text as="span" color={mutedColor}>—</Text>}
                                                        </Td>
                                                        <Td>
                                                            {s.ponding_band && s.ponding_band !== 'none'
                                                                ? <Badge colorScheme={pondBand(s.ponding_band).scheme} cursor="pointer" onClick={() => setTabIndex(TAB_TERRAIN)}><Icon as={MdWaterDrop} mr={0.5} />{fmtDepth(Number(s.ponding_depth_m))}</Badge>
                                                                : <Text fontSize="2xs" color={mutedColor}>{s.ponding_forecast_at ? 'clear' : '—'}</Text>}
                                                        </Td>
                                                        {/* --- END UPDATE */}
                                                        <Td>
                                                            <Badge colorScheme={Number(s.pothole_count) > 0 ? 'red' : 'green'}>
                                                                {s.pothole_count ?? 0}
                                                            </Badge>
                                                        </Td>
                                                        <Td textTransform="capitalize" fontSize="xs">
                                                            <Badge colorScheme={s.cracking_severity === 'alligator' ? 'red' : (!s.cracking_severity || s.cracking_severity === 'none') ? 'green' : 'orange'}>
                                                                {s.cracking_severity || 'none'}
                                                            </Badge>
                                                        </Td>
                                                        <Td fontFamily="mono" fontSize="xs">{s.rutting_depth_mm ?? 0} mm</Td>
                                                        <Td>
                                                            <HStack spacing={2}>
                                                                <Progress value={pci} size="xs" w="60px" borderRadius="full" colorScheme={pciColor(pci)} aria-label={`PCI ${pci}`} />
                                                                <Text fontSize="xs" fontWeight="bold">{s.segment_pci ?? '—'}</Text>
                                                            </HStack>
                                                        </Td>
                                                        {mlEvaluation && (
                                                            <Td fontSize="xs">
                                                                {ml ? (
                                                                    <Tooltip hasArrow label={(ml.reasons || []).join(' · ') || (ml.engine === 'ml' ? 'ML model' : 'FlowSense rules')}>
                                                                        <Badge colorScheme={ml.engine === 'ml' ? 'purple' : 'gray'} textTransform="none">
                                                                            {String(ml.recommended_action || '').replace(/_/g, ' ')}{ml.predicted_rul_years != null ? ` · ${ml.predicted_rul_years} yr` : ''}
                                                                        </Badge>
                                                                    </Tooltip>
                                                                ) : '—'}
                                                            </Td>
                                                        )}
                                                        {/* --- UPDATE (Drive Mode AI review): detections on this segment */}
                                                        <Td fontSize="xs">
                                                            {(() => {
                                                                const d = driveBySegment[String(s.id)];
                                                                if (!d || !d.total) return <Text fontSize="2xs" color={mutedColor}>—</Text>;
                                                                return (
                                                                    <Tooltip hasArrow label={`${d.confirmed} confirmed · ${d.pending} to review — open the Drive AI detections tab to see the photos`}>
                                                                        <HStack spacing={1} flexWrap="wrap" cursor="pointer" onClick={() => setTabIndex(TAB_DRIVE)}>
                                                                            <Badge colorScheme="purple">{d.total}</Badge>
                                                                            {d.by_type.slice(0, 3).map((t) => (
                                                                                <Badge key={t.type} colorScheme={damageInfo(t.type).scheme} variant={t.confirmed ? 'solid' : 'subtle'} fontSize="2xs">{damageInfo(t.type).label} {t.total}</Badge>
                                                                            ))}
                                                                        </HStack>
                                                                    </Tooltip>
                                                                );
                                                            })()}
                                                        </Td>
                                                        {/* --- END UPDATE */}
                                                    </Tr>
                                                );
                                            })}
                                        </Tbody>
                                    </Table>
                                </Box>
                            )}
                        </TabPanel>

                        {/* --- UPDATE (roads v1.3) TAB_TERRAIN: TERRAIN & PONDING */}
                        <TabPanel p={0}>
                            <RoadTerrainPondingTab roadId={activeRoadId} roadName={roadName || road?.name} segments={segments} onChanged={handleReportChanged} />
                        </TabPanel>
                        {/* --- END UPDATE */}

                        {/* TAB 3: SIDEWALKS & CORRIDOR SUB-ASSETS */}
                        <TabPanel p={0}>
                            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                                {subAssets.map((sub) => (
                                    <Box key={sub.id} p={3} borderWidth="1px" borderColor={borderColor} borderRadius="xl">
                                        <HStack justify="space-between">
                                            <Text fontWeight="bold" fontSize="xs" textTransform="capitalize">
                                                {String(sub.sub_asset_type || 'sub-asset').replace(/_/g, ' ')}
                                            </Text>
                                            <Badge colorScheme={sub.condition_rating === 'critical' ? 'red' : sub.condition_rating === 'poor' ? 'orange' : 'teal'}>
                                                {sub.condition_rating || 'unrated'}
                                            </Badge>
                                        </HStack>
                                        <Text fontSize="2xs" color={mutedColor} mt={1}>
                                            Side: {sub.side_of_road || '—'} • Material: {sub.material || '—'}
                                        </Text>
                                        <Text fontSize="2xs" color={mutedColor}>
                                            Survey Elevation: {sub.elevation_m ? `${sub.elevation_m}m` : '—'}
                                        </Text>
                                    </Box>
                                ))}
                                {subAssets.length === 0 && (
                                    <Text p={4} fontSize="xs" color={mutedColor}>No sub-assets (sidewalks, curbs) registered for this corridor.</Text>
                                )}
                            </SimpleGrid>
                        </TabPanel>

                        {/* TAB 3: ASPHALT LOGISTICS, TEMPERATURE, AND FLEET */}
                        <TabPanel p={0}>
                            <VStack align="stretch" spacing={4}>
                                <HStack justify="space-between" flexWrap="wrap" gap={2}>
                                    <Text fontSize="xs" fontWeight="700">Select Active Work Order</Text>
                                    <Select size="xs" maxW="320px" value={selectedWoId} onChange={(e) => handleSelectWo(e.target.value)} isDisabled={!workOrders.length} aria-label="Work order for logistics">
                                        {!workOrders.length && <option value="">No work orders</option>}
                                        {workOrders.map(wo => (
                                            <option key={wo.id} value={wo.id}>{shortId(wo.id)} • {wo.title}</option>
                                        ))}
                                    </Select>
                                </HStack>

                                <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} opacity={isLogisticsLoading ? 0.6 : 1}>
                                    <Stat p={3} borderWidth="1px" borderColor={borderColor} borderRadius="xl">
                                        <StatLabel fontSize="2xs">Asphalt Delivered</StatLabel>
                                        <StatNumber fontSize="lg" color="blue.500">{logistics.totalTonsDelivered} T</StatNumber>
                                    </Stat>
                                    <Stat p={3} borderWidth="1px" borderColor={borderColor} borderRadius="xl">
                                        <StatLabel fontSize="2xs">Trucks Logged</StatLabel>
                                        <StatNumber fontSize="lg">
                                            {logistics.asphaltLogs.length}
                                            {logistics.summary?.coldBatches ? <Text as="span" fontSize="xs" color="red.500"> ({logistics.summary.coldBatches} cold)</Text> : null}
                                        </StatNumber>
                                    </Stat>
                                    <Stat p={3} borderWidth="1px" borderColor={borderColor} borderRadius="xl">
                                        <StatLabel fontSize="2xs">Equipment Operating</StatLabel>
                                        <StatNumber fontSize="lg">{logistics.equipment.length} Units</StatNumber>
                                    </Stat>
                                    <Stat p={3} borderWidth="1px" borderColor={borderColor} borderRadius="xl">
                                        <StatLabel fontSize="2xs">Average Laydown Temp</StatLabel>
                                        <StatNumber fontSize="lg" color="orange.500">
                                            {logistics.asphaltLogs.length > 0
                                                ? `${Math.round(logistics.asphaltLogs.reduce((a, b) => a + Number(b.laydown_temp_c || 0), 0) / logistics.asphaltLogs.length)}°C`
                                                : '—'}
                                        </StatNumber>
                                    </Stat>
                                </SimpleGrid>

                                {logistics.equipment.length > 0 && (
                                    <HStack spacing={1} flexWrap="wrap">
                                        {logistics.equipment.map((eq, i) => (
                                            <Tag key={eq.id || `${eq.fleet_unit_id}-${i}`} size="sm" variant="subtle">
                                                {String(eq.equipment_type || 'unit').replace(/_/g, ' ')} · {eq.fleet_unit_id || '—'} · {eq.status || '—'}
                                            </Tag>
                                        ))}
                                    </HStack>
                                )}

                                <Text fontSize="xs" fontWeight="700">Asphalt Thermal Compaction Log</Text>
                                {logistics.asphaltLogs.length === 0 ? (
                                    <Text fontSize="xs" color={mutedColor}>No asphalt deliveries logged for this work order.</Text>
                                ) : (
                                    <Box overflowX="auto">
                                        <Table size="sm" variant="simple">
                                            <Thead bg={headerBg}>
                                                <Tr>
                                                    <Th>TRUCK / TICKET</Th>
                                                    <Th>TONS</Th>
                                                    <Th>PLANT DISPATCH</Th>
                                                    <Th>SITE ARRIVAL</Th>
                                                    <Th>LAYDOWN</Th>
                                                    <Th>COMPACTION</Th>
                                                    <Th>QA STATUS</Th>
                                                </Tr>
                                            </Thead>
                                            <Tbody>
                                                {logistics.asphaltLogs.map((log, i) => (
                                                    <Tr key={log.id ?? `${log.truck_identifier}-${i}`}>
                                                        <Td fontWeight="bold" fontSize="xs">{log.truck_identifier}{log.ticket_number ? ` / ${log.ticket_number}` : ''}</Td>
                                                        <Td fontSize="xs">{log.tons_delivered} T</Td>
                                                        <Td fontSize="xs">{log.plant_dispatch_temp_c ?? '—'}°C</Td>
                                                        <Td fontSize="xs" fontWeight="bold" color={Number(log.arrival_temp_c) < 135 ? 'red.500' : 'inherit'}>
                                                            {log.arrival_temp_c}°C
                                                        </Td>
                                                        <Td fontSize="xs">{log.laydown_temp_c ?? '—'}°C</Td>
                                                        <Td fontSize="xs">{log.compaction_temp_c ?? '—'}°C</Td>
                                                        <Td>
                                                            <Tooltip hasArrow label={log.rejection_reason || ''} isDisabled={!log.rejection_reason}>
                                                                <Badge colorScheme={log.delivery_status === 'completed' ? 'green' : 'red'}>
                                                                    {String(log.delivery_status || '').replace(/_/g, ' ')}
                                                                </Badge>
                                                            </Tooltip>
                                                        </Td>
                                                    </Tr>
                                                ))}
                                            </Tbody>
                                        </Table>
                                    </Box>
                                )}
                            </VStack>
                        </TabPanel>

                        {/* TAB 4: WORK ORDERS */}
                        <TabPanel p={0}>
                            <VStack align="stretch" spacing={2}>
                                {workOrders.length === 0 && <Text p={4} fontSize="xs" color={mutedColor}>No work orders are linked to this road.</Text>}
                                {workOrders.map((wo) => (
                                    <Flex key={wo.id} p={3} borderWidth="1px" borderColor={borderColor} borderRadius="xl" justify="space-between" align="center" gap={2} wrap="wrap">
                                        <Box minW={0} flex="1">
                                            <HStack flexWrap="wrap">
                                                <Text fontWeight="800" fontSize="xs">{wo.title}</Text>
                                                <Badge colorScheme={wo.priority === 'critical' ? 'red' : wo.priority === 'high' ? 'orange' : 'gray'} fontSize="2xs">{wo.priority}</Badge>
                                                <Badge colorScheme="blue" variant="outline" fontSize="2xs">{wo.work_order_type || 'corrective'}</Badge>
                                                {wo.category && <Badge variant="subtle" fontSize="2xs">{wo.category}</Badge>}
                                                {Number(wo.qa_audit_count) > 0 && <Badge colorScheme="teal" fontSize="2xs">QA ✓</Badge>}
                                            </HStack>
                                            <Text fontSize="2xs" color={mutedColor}>
                                                Assigned: {wo.assigned_user_username || 'Unassigned'} • Due: {wo.due_date ? String(wo.due_date).slice(0, 10) : 'No Date'}
                                            </Text>
                                        </Box>
                                        <HStack spacing={1}>
                                            <Badge colorScheme={wo.status === 'completed' ? 'green' : 'yellow'}>{wo.status}</Badge>
                                            {/* --- UPDATE (Completed work) */}
                                            {isDoneWo(wo) && (
                                                <Tooltip hasArrow label="Completed work — photos, notes and checklist">
                                                    <IconButton size="xs" variant="ghost" colorScheme="green" icon={<Icon as={MdPhotoLibrary} />} aria-label={`Completed work and photos for ${wo.title}`} onClick={() => openCompletedJob(wo.id)} />
                                                </Tooltip>
                                            )}
                                            {/* --- END UPDATE */}
                                            <Tooltip hasArrow label="Inspection / acceptance checklist">
                                                <IconButton size="xs" variant="ghost" icon={<Icon as={MdChecklist} />} aria-label={`Checklist for ${wo.title}`} onClick={() => openChecklist({ workOrder: wo, category: wo.category })} />
                                            </Tooltip>
                                            {isPavingJob(wo) && (
                                                <Tooltip hasArrow label="AI workmanship QA audit">
                                                    <IconButton size="xs" variant="ghost" colorScheme="teal" icon={<Icon as={MdVerified} />} aria-label={`QA audit for ${wo.title}`} onClick={() => openQa(wo)} />
                                                </Tooltip>
                                            )}
                                            <Tooltip hasArrow label="Open in Work Orders">
                                                <IconButton size="xs" variant="ghost" icon={<Icon as={MdOpenInNew} />} aria-label={`Open ${wo.title}`} onClick={() => navigate(`/admin/workorders?search=${encodeURIComponent(wo.id)}`)} />
                                            </Tooltip>
                                        </HStack>
                                    </Flex>
                                ))}
                                <Button size="xs" alignSelf="flex-start" leftIcon={<Icon as={MdAdd} />} variant="outline" onClick={() => openCreateWorkOrder(navigate, roadFollowUpDraft({ road: roadInfo, action: 'pothole' }))}>
                                    New road work order
                                </Button>
                            </VStack>
                        </TabPanel>

                        {/* TAB 5: INSPECTIONS */}
                        <TabPanel p={0}>
                            <VStack align="stretch" spacing={2}>
                                <HStack justify="space-between">
                                    <Text fontSize="xs" color={mutedColor}>Tick-box inspections and acceptance checks for this road and its jobs.</Text>
                                    <Button size="xs" colorScheme="blue" leftIcon={<Icon as={MdChecklist} />} onClick={() => openChecklist({ category: 'Inspection' })}>New inspection</Button>
                                </HStack>
                                {checklists.length === 0 && <Text p={4} fontSize="xs" color={mutedColor}>No inspections recorded yet.</Text>}
                                {checklists.map((c) => {
                                    const wo = c.work_order_id ? workOrders.find((w) => String(w.id) === String(c.work_order_id)) : null;
                                    return (
                                        <Flex key={c.id} p={3} borderWidth="1px" borderColor={borderColor} borderRadius="xl" justify="space-between" align="center" gap={2} wrap="wrap">
                                            <Box minW={0} flex="1">
                                                <HStack flexWrap="wrap">
                                                    <Text fontWeight="800" fontSize="xs">{TEMPLATE_LABEL[c.template_key] || c.template_key}</Text>
                                                    <Badge colorScheme={c.status === 'submitted' ? 'green' : 'yellow'} fontSize="2xs">{c.status}</Badge>
                                                    {c.score != null && <Badge colorScheme={RATING_COLOR[c.rating] || 'gray'} fontSize="2xs">{c.score} · {c.rating}</Badge>}
                                                    {c.summary?.failed > 0 && <Badge colorScheme="red" variant="outline" fontSize="2xs">{c.summary.failed} defect(s)</Badge>}
                                                </HStack>
                                                <Text fontSize="2xs" color={mutedColor}>
                                                    {c.submitted_at ? `Submitted ${new Date(c.submitted_at).toLocaleString()}` : `Updated ${c.updated_at ? new Date(c.updated_at).toLocaleString() : '—'} · ${c.completion_pct ?? 0}% checked`}
                                                    {wo ? ` • WO ${wo.title}` : ''}
                                                    {c.outcome?.auto_work_order?.workOrderId ? ` • raised ${c.outcome.auto_work_order.workOrderId}` : ''}
                                                </Text>
                                            </Box>
                                            <Button size="xs" variant="outline" onClick={() => openChecklist({ checklistId: c.id, workOrder: wo || undefined })}>
                                                {c.status === 'submitted' ? 'View' : 'Continue'}
                                            </Button>
                                        </Flex>
                                    );
                                })}
                            </VStack>
                        </TabPanel>

                        {/* --- UPDATE (Drive Mode AI review) TAB 6: DRIVE MODE AI DETECTIONS */}
                        <TabPanel p={0}>
                            <RoadDriveDetectionsPanel roadId={activeRoadId} roadName={roadName || road?.name} onDataChanged={handleDriveChanged} embedded />
                        </TabPanel>
                        {/* --- END UPDATE */}

                        {/* --- UPDATE (Completed work) TAB 7: COMPLETED JOBS + INSPECTIONS WITH PHOTOS */}
                        <TabPanel p={0}>
                            <RoadCompletedWorkPanel
                                roadId={activeRoadId}
                                roadName={roadName || road?.name}
                                road={roadInfo}
                                knownWorkOrderIds={workOrderIds}
                                focusJobId={completedFocus}
                                onFocusHandled={clearCompletedFocus}
                                onOpenChecklist={openChecklist}
                                onDataChanged={handleReportChanged}
                            />
                        </TabPanel>
                        {/* --- END UPDATE */}
                    </TabPanels>
                </Tabs>
            )}

            <RoadInspectionChecklist
                isOpen={isChecklistOpen}
                onClose={onChecklistClose}
                roadId={activeRoadId}
                roadName={roadName || road?.name}
                road={roadInfo}
                workOrder={checklistTarget?.workOrder}
                checklistId={checklistTarget?.checklistId}
                category={checklistTarget?.category}
                onSubmitted={handleReportChanged}
            />
            <RoadWorkOrderQaModal
                isOpen={isQaOpen}
                onClose={onQaClose}
                workOrder={qaWorkOrder}
                road={roadInfo}
                onAuditComplete={handleReportChanged}
                onOpenChecklist={(wo) => openChecklist({ workOrder: wo, category: wo?.category })}
            />
        </Box>
    );
};

RoadWorksReportPanel.propTypes = {
    activeRoadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    roadName: PropTypes.string,
    embedded: PropTypes.bool,
    onDataChanged: PropTypes.func,
};

export default memo(RoadWorksReportPanel);